import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type BannedVisitor, type Moderation } from "../../api";
import { dateTime, visitorLabel } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { ReadOnlyNote, Section, SettingRow, SitePicker, StatusLine, Switch, useSelectedSite, useSites, useStatus } from "./common";

/**
 * What a visitor's message may not contain, per site. The rules are enforced
 * on the server before anything is stored, and the widget explains a refusal
 * in the visitor's own language.
 */

function parseWords(value: string): string[] {
  return [...new Set(value.split("\n").map((w) => w.trim()).filter((w) => w.length >= 2))].slice(0, 100);
}

/**
 * Everyone banned on the operator's sites, with the way back.
 *
 * Not admin-only, because banning is not: whoever can ban from a conversation
 * can undo it here — which matters most for the ban made by mistake, since a
 * banned visitor opens no new conversation to undo it from.
 */
function BansSection(): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.spam;
  const [bans, setBans] = useState<BannedVisitor[] | null>(null);
  const [status, show, fail] = useStatus();

  const load = useCallback(async () => {
    try {
      setBans((await api.bans()).bans);
    } catch {
      show("error", t.bansFailed);
    }
  }, [show, t.bansFailed]);

  useEffect(() => {
    void load();
  }, [load]);

  async function unban(ban: BannedVisitor): Promise<void> {
    try {
      await api.unbanVisitor(ban.visitorId);
      show("ok", all.inbox.unbannedToast);
      await load();
    } catch (err) {
      fail(err);
    }
  }

  return (
    <Section title={t.bansTitle} intro={t.bansIntro}>
      {bans === null ? null : bans.length === 0 ? (
        <p class="hint">{t.bansNone}</p>
      ) : (
        <ul class="item-list">
          {bans.map((ban) => (
            <li key={ban.visitorId}>
              <div class="item-main">
                <span>
                  <strong>
                    {visitorLabel({ visitorName: ban.displayName, visitorEmail: ban.email, visitorId: ban.visitorId }, lang)}
                  </strong>
                  <span class="chip">{ban.siteName}</span>
                </span>
                {ban.displayName && ban.email ? <span class="hint">{ban.email}</span> : null}
                {ban.reason ? <span class="item-body">{t.banReason(ban.reason)}</span> : null}
                {ban.bannedAt ? <span class="hint">{t.bannedAt(dateTime(ban.bannedAt, lang))}</span> : null}
              </div>
              <div class="item-actions">
                <button class="btn sm" onClick={() => void unban(ban)}>
                  {all.context.unban}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <StatusLine status={status} />
    </Section>
  );
}
export function SpamTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.spam;
  const { sites, reload } = useSites();
  const [site, selectSite] = useSelectedSite(sites);
  const [words, setWords] = useState("");
  const [rules, setRules] = useState<Moderation | null>(null);
  const [status, show, fail] = useStatus();

  useEffect(() => {
    if (!site) return;
    setRules(site.moderation);
    setWords(site.moderation.blockedWords.join("\n"));
  }, [site?.id, site?.moderation]);

  if (!site || !rules) return <div class="empty">{all.common.createSiteFirst}</div>;
  // (The ban list needs a site to have visitors on, so it shares this guard.)

  const next: Moderation = { ...rules, blockedWords: parseWords(words) };
  const dirty = JSON.stringify(next) !== JSON.stringify(site.moderation);
  const disabled = !isAdmin;

  async function save(): Promise<void> {
    try {
      await api.updateSite(site!.id, { moderation: next });
      await reload();
      show("ok", t.saved);
    } catch (err) {
      fail(err);
    }
  }

  return (
    <>
      <ReadOnlyNote isAdmin={isAdmin} />
      <SitePicker sites={sites} value={site} onChange={selectSite} />
      <Section title={t.wordsTitle} intro={t.wordsIntro}>
        <SettingRow title={t.wordsLabel} hint={t.wordsHint} htmlFor="spam-words" wide>
          <textarea
            id="spam-words"
            class="plain"
            rows={6}
            value={words}
            disabled={disabled}
            onInput={(e) => setWords(e.currentTarget.value)}
          />
        </SettingRow>
      </Section>
      <Section title={t.rulesTitle}>
        <Switch
          label={t.links}
          hint={t.linksHint}
          checked={rules.blockLinks}
          disabled={disabled}
          onChange={(v) => setRules({ ...rules, blockLinks: v })}
        />
        <label>
          {t.repeats} <span class="hint">{t.repeatsHint}</span>
          <input
            type="number"
            min={0}
            max={20}
            value={rules.maxRepeats}
            disabled={disabled}
            style={{ maxWidth: "120px", display: "block" }}
            onInput={(e) => setRules({ ...rules, maxRepeats: Math.max(0, Math.min(20, Number(e.currentTarget.value) || 0)) })}
          />
        </label>
      </Section>
      {isAdmin ? (
        <div class="save-bar">
          <button class="btn primary" disabled={!dirty} onClick={() => void save()}>
            {all.common.saveChanges}
          </button>
          {dirty ? (
            <button
              class="btn"
              onClick={() => {
                setRules(site.moderation);
                setWords(site.moderation.blockedWords.join("\n"));
              }}
            >
              {all.common.cancel}
            </button>
          ) : null}
        </div>
      ) : null}
      <StatusLine status={status} />
      <BansSection />
    </>
  );
}
